// Restored from production OTA 2026-08-21 (module 1408).
//
// Replaces a Firebase v8 template that no longer matched the app: it stored the
// user under auth.info, dispatched an undefined UPDATE_USER_PROFILE type and
// lacked signOff, googleLogin, the mobile OTP and email flows, wallet history
// and updateAuthMobile, all of which the screens call.
import {
  FETCH_USER,
  FETCH_USER_SUCCESS,
  FETCH_USER_FAILED,
  USER_SIGN_IN,
  USER_SIGN_IN_FAILED,
  USER_SIGN_OUT,
  CLEAR_LOGIN_ERROR,
  REQUEST_OTP,
  REQUEST_OTP_SUCCESS,
  REQUEST_OTP_FAILED,
  UPDATE_USER_WALLET_HISTORY,
  SEND_RESET_EMAIL,
  SEND_RESET_EMAIL_FAILED
} from "../store/types";
import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { onValue, update, off, push, child } from "firebase/database";
import {
  onAuthStateChanged,
  signInWithCredential,
  signInWithPopup,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithCustomToken
} from "firebase/auth";
import { uploadBytesResumable, getDownloadURL } from "firebase/storage";
import base64 from 'react-native-base64';
import AppCat from '../config/AppCat';

// Hosting functions are called on the origin the web admin is served from, and
// on the project's default hosting domain everywhere else (the apps).
const functionsHost = () => {
  const { config } = firebase;
  const settings = store.getState().settingsdata.settings;
  if (typeof window !== 'undefined' && window.location && settings && settings.CompanyWebsite === window.location.origin) {
    return window.location.origin;
  }
  return `https://${config.projectId}.web.app`;
};

const basicAuthHeader = () => {
  const { config } = firebase;
  return "Basic " + base64.encode(config.projectId + ":" + AppCat);
};

const language = () => store.getState().languagedata.defaultLanguage;

export const fetchUser = () => (dispatch) => {
  const { auth, singleUserRef } = firebase;

  dispatch({
    type: FETCH_USER,
    payload: null
  });
  onAuthStateChanged(auth, (user) => {
    if (!user) {
      dispatch({
        type: FETCH_USER_FAILED,
        payload: { code: language().auth_error, message: language().not_logged_in }
      });
      return;
    }
    onValue(singleUserRef(user.uid), async (snapshot) => {
      if (snapshot.val()) {
        const profile = snapshot.val();
        profile.uid = user.uid;
        dispatch({
          type: FETCH_USER_SUCCESS,
          payload: profile
        });
        return;
      }

      // Signed in but no users/{uid} record yet: build what the auth provider
      // knows and let check_auth_exists create (or find) the record.
      const data = {
        uid: user.uid,
        mobile: '',
        email: '',
        firstName: '',
        lastName: '',
        verifyId: ''
      };
      if (user.providerData.length == 0 && user.email) {
        data.email = user.email;
      }
      if (user.providerData.length > 0 && user.phoneNumber) {
        data.mobile = user.phoneNumber;
      }
      if (user.providerData.length > 0) {
        const provider = user.providerData[0];
        if (provider.providerId == 'phone') {
          data.mobile = provider.phoneNumber;
        }
        if (provider.providerId == 'google.com' || provider.providerId == 'apple.com') {
          if (provider.email) {
            data.email = provider.email;
          }
          if (provider.phoneNumber) {
            data.mobile = provider.phoneNumber;
          }
          if (provider.displayName) {
            if (provider.displayName.split(' ').length > 0) {
              data.firstName = provider.displayName.split(' ')[0];
              data.lastName = provider.displayName.split(' ')[1];
            } else {
              data.firstName = provider.displayName;
            }
          }
          if (provider.photoURL) {
            data.profile_image = provider.photoURL;
          }
        }
      }
      if (user.providerData.length > 0 && user.verifyId) {
        data.verifyId = user.verifyId;
      }

      const response = await fetch(`${functionsHost()}/check_auth_exists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': basicAuthHeader()
        },
        body: JSON.stringify({
          data: JSON.stringify(data)
        })
      });
      const json = await response.json();
      if (json.uid) {
        dispatch({
          type: FETCH_USER_SUCCESS,
          payload: json
        });
      } else {
        dispatch({
          type: FETCH_USER_FAILED,
          payload: { code: language().auth_error, message: json.error }
        });
      }
    });
  });
};

export const validateReferer = async (referralId) => {
  const { config } = firebase;
  const response = await fetch(`https://${config.projectId}.web.app/validate_referrer`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ referralId })
  });
  return await response.json();
};

export const checkUserExists = async (regData) => {
  const response = await fetch(`${functionsHost()}/check_user_exists`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': basicAuthHeader()
    },
    body: JSON.stringify({
      email: regData.email,
      mobile: regData.mobile
    })
  });
  return await response.json();
};

export const mainSignUp = async (regData) => {
  const { config } = firebase;
  const response = await fetch(`https://${config.projectId}.web.app/user_signup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ regData })
  });
  return await response.json();
};

export const updateProfileWithEmail = (profileData) => async (dispatch) => {
  try {
    const response = await fetch(`${functionsHost()}/update_user_email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': basicAuthHeader()
      },
      body: JSON.stringify(profileData)
    });
    const result = await response.json();
    if (result.error) {
      return { success: false, error: result.error };
    }
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

export const updateCountry = (uid, country) => async (dispatch) => {
  try {
    const response = await fetch(`${functionsHost()}/update_user_country`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': basicAuthHeader()
      },
      body: JSON.stringify({ uid, country })
    });
    const result = await response.json();
    if (result.error) {
      return { success: false, error: result.error };
    }
    return { success: true, message: 'Country updated successfully' };
  } catch (error) {
    return { success: false, error };
  }
};

export const requestPhoneOtpDevice = (verificationId) => async (dispatch) => {
  dispatch({
    type: REQUEST_OTP_SUCCESS,
    payload: verificationId
  });
};

export const mobileSignIn = (verficationId, code) => (dispatch) => {
  const { auth, mobileAuthCredential } = firebase;

  dispatch({
    type: USER_SIGN_IN,
    payload: null
  });
  signInWithCredential(auth, mobileAuthCredential(verficationId, code))
    .then(() => {})
    .catch((error) => {
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: error
      });
    });
};

/**
 * Remembers a named place (e.g. Home/Work) under the user's savedAddresses,
 * replacing an existing entry with the same name.
 */
export const saveAddresses = async (uid, location, name) => {
  const { singleUserRef } = firebase;
  const entry = {
    description: location.add,
    lat: location.lat,
    lng: location.lng,
    count: 1,
    name: name
  };
  onValue(child(singleUserRef(uid), 'savedAddresses'), (snapshot) => {
    if (!snapshot.val()) {
      push(child(singleUserRef(uid), 'savedAddresses'), entry);
      return;
    }
    const addresses = snapshot.val();
    let notFound = true;
    for (let key in addresses) {
      if (addresses[key].name == name) {
        update(child(singleUserRef(uid), 'savedAddresses/' + key), entry);
        notFound = false;
      }
    }
    if (notFound) {
      push(child(singleUserRef(uid), 'savedAddresses'), entry);
    }
  }, { onlyOnce: true });
};

export const googleLogin = (idToken, accessToken) => (dispatch) => {
  const { auth, googleCredential } = firebase;

  dispatch({
    type: USER_SIGN_IN,
    payload: null
  });
  signInWithCredential(auth, googleCredential(idToken, accessToken))
    .then(() => {})
    .catch((error) => {
      console.log(error);
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: error
      });
    });
};

export const appleSignIn = (credentialData) => (dispatch) => {
  const { auth, appleProvider } = firebase;

  dispatch({
    type: USER_SIGN_IN,
    payload: null
  });
  if (credentialData) {
    signInWithCredential(auth, appleProvider.credential(credentialData))
      .then(() => {})
      .catch((error) => {
        dispatch({
          type: USER_SIGN_IN_FAILED,
          payload: error
        });
      });
  } else {
    signInWithPopup(auth, appleProvider)
      .then((result) => {
        signInWithCredential(auth, result.credential)
          .then(() => {})
          .catch((error) => {
            dispatch({
              type: USER_SIGN_IN_FAILED,
              payload: error
            });
          });
      })
      .catch((error) => {
        dispatch({
          type: USER_SIGN_IN_FAILED,
          payload: error
        });
      });
  }
};

export const signOff = () => (dispatch) => {
  const { auth, singleUserRef, walletHistoryRef, userNotificationsRef } = firebase;
  const uid = auth.currentUser.uid;

  off(singleUserRef(uid));
  off(walletHistoryRef(uid));
  off(userNotificationsRef(uid));

  const finish = () => {
    firebaseSignOut(auth)
      .then(() => {
        dispatch({
          type: USER_SIGN_OUT,
          payload: null
        });
      })
      .catch(() => {});
  };

  onValue(singleUserRef(uid), (snapshot) => {
    const profile = snapshot.val();
    if (profile && profile.usertype === 'driver') {
      // Take the driver off duty first so no trip is offered to a signed-out
      // device, then sign out once that write has had time to land.
      update(singleUserRef(uid), { driverActiveStatus: false });
      setTimeout(finish, 2000);
    } else {
      finish();
    }
  }, { onlyOnce: true });
};

// The login screen and web admin still call signOut.
export const signOut = signOff;

export const updateProfile = (updateData) => async (dispatch) => {
  const { auth, singleUserRef, driverDocsRef, driverDocsRefBack, verifyIdImageRef } = firebase;
  const uid = auth.currentUser.uid;

  if (updateData.licenseImage) {
    await uploadBytesResumable(driverDocsRef(uid), updateData.licenseImage);
    updateData.licenseImage = await getDownloadURL(driverDocsRef(uid));
  }
  if (updateData.licenseImageBack) {
    await uploadBytesResumable(driverDocsRefBack(uid), updateData.licenseImageBack);
    updateData.licenseImageBack = await getDownloadURL(driverDocsRefBack(uid));
  }
  if (updateData.verifyIdImage) {
    await uploadBytesResumable(verifyIdImageRef(uid), updateData.verifyIdImage);
    updateData.verifyIdImage = await getDownloadURL(verifyIdImageRef(uid));
  }

  const profile = store.getState().auth.profile;
  if (profile) {
    dispatch({
      type: FETCH_USER_SUCCESS,
      payload: { ...profile, ...updateData }
    });
  }
  update(singleUserRef(uid), updateData);
};

export const updateProfileImage = (imageBlob) => {
  const { auth, singleUserRef, profileImageRef } = firebase;
  const uid = auth.currentUser.uid;

  uploadBytesResumable(profileImageRef(uid), imageBlob)
    .then(() => {
      imageBlob.close();
      return getDownloadURL(profileImageRef(uid));
    })
    .then((url) => {
      update(singleUserRef(uid), { profile_image: url });
    });
};

export const updateWebProfileImage = async (imageBlob) => {
  const { auth, singleUserRef, profileImageRef } = firebase;
  const uid = auth.currentUser.uid;

  await uploadBytesResumable(profileImageRef(uid), imageBlob);
  const image = await getDownloadURL(profileImageRef(uid));
  update(singleUserRef(uid), { profile_image: image });
};

export const updateCustomerProfileImage = async (imageBlob, id) => {
  const { singleUserRef, profileImageRef } = firebase;

  await uploadBytesResumable(profileImageRef(id), imageBlob);
  const image = await getDownloadURL(profileImageRef(id));
  update(singleUserRef(id), { profile_image: image });
};

export const updatePushToken = (token, platform) => {
  const { auth, singleUserRef } = firebase;
  update(singleUserRef(auth.currentUser.uid), {
    pushToken: token,
    userPlatform: platform
  });
};

export const clearLoginError = () => (dispatch) => {
  dispatch({
    type: CLEAR_LOGIN_ERROR,
    payload: null
  });
};

const dispatchWalletHistory = (dispatch, uid) => {
  const { walletHistoryRef } = firebase;
  onValue(walletHistoryRef(uid), (snapshot) => {
    const data = snapshot.val();
    if (data) {
      const arr = Object.keys(data).map((i) => {
        data[i].id = i;
        return data[i];
      });
      dispatch({
        type: UPDATE_USER_WALLET_HISTORY,
        payload: arr.reverse()
      });
    }
  });
};

export const fetchWalletHistory = () => (dispatch) => {
  dispatchWalletHistory(dispatch, firebase.auth.currentUser.uid);
};

export const fetchUserWalletHistory = (userId) => (dispatch) => {
  dispatchWalletHistory(dispatch, userId);
};

export const sendResetMail = (email) => async (dispatch) => {
  const { authRef } = firebase;

  dispatch({
    type: SEND_RESET_EMAIL,
    payload: email
  });
  sendPasswordResetEmail(authRef(), email)
    .then(() => {
      console.log('Email send successfuly');
    })
    .catch(() => {
      dispatch({
        type: SEND_RESET_EMAIL_FAILED,
        payload: { code: language().auth_error, message: language().not_registred }
      });
    });
};

export const verifyEmailPassword = (email, pass) => async (dispatch) => {
  const { authRef } = firebase;

  signInWithEmailAndPassword(authRef(), email, pass)
    .then(() => {})
    .catch((error) => {
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: error
      });
    });
};

export const requestMobileOtp = (mobile) => async (dispatch) => {
  dispatch({
    type: REQUEST_OTP,
    payload: true
  });
  try {
    const response = await fetch(`${functionsHost()}/request_mobile_otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ mobile })
    });
    const result = await response.json();
    if (result.success) {
      dispatch({
        type: REQUEST_OTP_SUCCESS,
        payload: true
      });
    } else {
      dispatch({
        type: REQUEST_OTP_FAILED,
        payload: result.error
      });
    }
  } catch (error) {
    console.log(error);
  }
};

export const verifyMobileOtp = (mobile, otp) => async (dispatch) => {
  const { auth } = firebase;
  try {
    const response = await fetch(`${functionsHost()}/verify_mobile_otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ mobile, otp })
    });
    const result = await response.json();
    if (result.token) {
      signInWithCustomToken(auth, result.token)
        .then(() => {})
        .catch((error) => {
          dispatch({
            type: USER_SIGN_IN_FAILED,
            payload: error
          });
        });
    } else {
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: result.error
      });
    }
  } catch (error) {
    console.log(error);
    dispatch({
      type: USER_SIGN_IN_FAILED,
      payload: error
    });
  }
};

export const updateAuthMobile = async (mobile, otp) => {
  const { auth } = firebase;
  try {
    const response = await fetch(`${functionsHost()}/update_auth_mobile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ uid: auth.currentUser.uid, mobile, otp })
    });
    const result = await response.json();
    if (result.success) {
      return { success: true };
    }
    return { success: false, error: result.error };
  } catch (error) {
    return { success: false, error };
  }
};

export const addToWallet = async (uid, amount, description, transactionId) => {
  try {
    const response = await fetch(`${functionsHost()}/add_to_wallet`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        uid,
        amount,
        description,
        transaction_id: transactionId
      })
    });
    const result = await response.json();
    if (result.success) {
      return { success: true };
    }
    return { success: false, error: result.error };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * transfer_wallet now authenticates the caller and ignores from_uid and
 * withdraw_amount (see functions/common/walletTransfer.js). The parameters are
 * kept so existing callers still line up; the amount is in the sender's
 * currency.
 */
export const transferWallet = async (fromUid, toUid, amount, description, withdrawAmount) => {
  const { auth } = firebase;
  try {
    const token = await auth.currentUser.getIdToken();
    const response = await fetch(`https://us-central1-${firebase.config.projectId}.cloudfunctions.net/transfer_wallet`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        to_uid: toUid,
        amount: withdrawAmount || amount,
        description
      })
    });
    const result = await response.json();
    if (result.success) {
      return { success: true };
    }
    return { success: false, error: result.error };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getUserDetails = async (uid) => {
  try {
    const response = await fetch(`${functionsHost()}/get_user_details`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ uid })
    });
    const result = await response.json();
    if (result.success) {
      return { success: true, user: result.user };
    }
    return { success: false, error: result.error };
  } catch (error) {
    return { success: false, error: error.message };
  }
};
